<template>
  <div>
    <div v-if="activeTimetableProject && !$wait.waiting('management-tt-init')" class="w-100">
      <slot name="heading">
        <div class="small text-muted text-uppercase mb-2 w-100">
          {{ $t('management.timetable_project_switch.heading') }}
        </div>
      </slot>

      <SDropDown id="tt-project-switch">
        <template #button-content>
          <span class="btn btn-md with-icon">
            <span class="d-flex align-items-center">
              <span>{{ activeTimetableProject.getProjectName() }}</span>
              <sdui-icon class="icon-chevron-down ml-2 mr-n1 icon-24" />
            </span>
          </span>
        </template>
        <b-dropdown-item-button v-for="project in projects"
                                :key="project.id"
                                @click="selectProject(project)"
        >
          {{ project.name || '2019 / 2020' }}
        </b-dropdown-item-button>
        <li class="dropdown-divider" aria-hidden="true" />
        <b-dropdown-item-button @click="showNewTTProject">
          {{ $t('management.timetable_project_switch.new_school_year') }}
        </b-dropdown-item-button>
      </SDropDown>
    </div>

    <div v-else-if="$wait.waiting('select-tt-project') || $wait.waiting('management-tt-init')" class="m-4 p-5">
      <sdui-loader />
    </div>

    <SModal
      v-model="showNewSchoolyear"
      :header-title="$t('management.timetable_project_switch.new_projekt')"
      size="lg"
      return-focus="#tt-project-switch__BV_toggle_"
      :show-footer-gradient="false"
    >
      <template v-if="showNewSchoolyear">
        <h2 class="h4 font-weight-bold">{{ $t('management.timetable_project_switch.choose_new_school_year') }}</h2>

        <VForm id="form-tt-project-switch-new-schoolyear" @submit="saveNewTTProject">
          <div v-if="newTTProject" class="d-flex justify-content-around">
            <div class="w-50 pt-2 mb-4">
              <SDatePicker
                id="begins_at"
                v-model="newTTProject.begins_at"
                :validation-rules="`required|isBefore:${newTTProject.ends_at}`"
                validation-field-name="begins_at"
                :label="$t('management.timetable_project_switch.begins_at')"
                :min-date="dateRangeBySuggestedSchoolYear.minDateBeginsAt"
                :max-date="dateRangeBySuggestedSchoolYear.maxDateBeginsAt"
              />
            </div>

            <div class="w-25" />

            <div class="w-50 pt-2 mb-4">
              <SDatePicker
                id="ends_at"
                v-model="newTTProject.ends_at"
                :validation-rules="`required|isAfter:${newTTProject.begins_at}`"
                validation-field-name="ends_at"
                :label="$t('management.timetable_project_switch.ends_at')"
                :min-date="dateRangeBySuggestedSchoolYear.minDateEndsAt"
                :max-date="dateRangeBySuggestedSchoolYear.maxDateEndsAt"
              />
            </div>
          </div>
        </VForm>

        <SListItem
          :title="$t('management.timetable_project_switch.overtake_basedata')"
          :description="$t('management.timetable_project_switch.basedata.description')"
        >
          <template #right-action>
            <SCheckbox
              v-model="newTTProjectsCopyData"
              name="copy-tt-data"
              :label-text="$t('management.timetable_project_switch.overtake_basedata')"
              checkbox-type="switch"
            />
          </template>
        </SListItem>

        <b-alert v-if="!newTTProjectsCopyData"
                 show
                 variant="warning"
        >
          {{ $t('management.timetable_project_switch.warning') }}
        </b-alert>
      </template>

      <template #modal-footer>
        <div class="d-flex align-items-center justify-content-end">
          <div v-if="$wait.waiting('new-tt-project')" class="m-4 position-relative">
            <sdui-loader />
          </div>
          <!-- eslint-disable-next-line sdui/require-focus-restore-on-disabled-button -->
          <b-button class="mx-3"
                    :disabled="$wait.waiting('new-tt-project')"
                    @click="showNewSchoolyear = false"
          >
            {{ $t('management.timetable_project_switch.cancel') }}
          </b-button>
          <!-- eslint-disable-next-line sdui/require-focus-restore-on-disabled-button -->
          <b-button
            :disabled="$wait.waiting('new-tt-project')"
            variant="primary"
            type="submit"
            form="form-tt-project-switch-new-schoolyear"
          >
            {{ $t('management.timetable_project_switch.submit') }}
          </b-button>
        </div>
      </template>
    </SModal>
  </div>
</template>
<script>
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import timetableMeta from 'src/mixins/timetable/timetableMeta.js'
import { TimetableMeta, Grade } from 'src/store/models/timetable/index.js'
import SDatePicker from 'src/components/base/datepicker/SDatePicker.vue'
import schoolYearMixin from 'src/mixins/management/schoolYearMixin.js'
import SCheckbox from 'src/components/base/SCheckbox.vue'
import { getZeroSafeId } from 'src/mixins/timetable/timetablePageOrganiser.js'

export default {
  name: 'TimetableProjectSwitch',
  components: { SduiLoader, SDatePicker, SCheckbox },
  mixins: [timetableMeta, schoolYearMixin],
  emits: ['project-switched'],
  props: {
    school: {
      type: Object,
      default: null,
    },

    showPopup: {
      type: Boolean,
    },
  },

  data: () => ({
    newTTProject: null,
    newTTProjectsCopyData: true,
    showNewSchoolyear: false,
  }),

  computed: {
    projects() {
      return this.$store.state.timetablePlanner.projects
    },
  },

  methods: {
    openNewTimetablePage() {
      const grade = Grade.query().first()
      if (!grade) return
      this.$router.replace({ name: 'management-timetable', params: { entity: 'grades', id: grade.id } })
    },

    saveNewTTProject: waitFor('new-tt-project', async function () {
      const findProject = (searchBeginsAt, searchEndsAt) => {
        return this.projects
          .filter((el) => el.name)
          .find((p) => {
            const [beginsAt, endsAt] = p.name.split(' / ')
            return beginsAt === this.$dateFormat.yearlong(searchBeginsAt) && endsAt === this.$dateFormat.yearlong(searchEndsAt)
          })
      }
      const schoolyearExists = findProject(this.newTTProject.begins_at, this.newTTProject.ends_at)

      if (schoolyearExists) {
        this.$toastNotify({
          type: 'error',
          text: this.$t('management.timetable_project_switch.notifications.school_year_exists'),
          duration: 4000,
        })
        return
      }

      // get project id to copy
      const projectId = findProject(this.activeTimetableProject.begins_at, this.activeTimetableProject.ends_at)?.id

      if (!projectId && this.newTTProjectsCopyData) {
        this.$toastNotify({
          type: 'error',
          text: 'Daten konnten nicht kopiert werden',
          duration: 4000,
        })
        return
      }

      await this.$store.dispatch('timetablePlanner/createTimetableProject', {
        timetableMeta: this.newTTProject,
        copyVersion: this.newTTProjectsCopyData ? projectId : null,
      })

      this.showNewSchoolyear = false

      this.$toastNotify({
        type: 'success',
        text: this.$t('management.timetable_project_switch.notifications.success'),
        duration: 5000,
      })

      this.rememberClosedState('hideTimetablePlannerCard-2021')
      this.$emit('project-switched')
      this.openNewTimetablePage()
    }),

    rememberClosedState(key) {
      if (!localStorage) return
      localStorage.setItem(key, 'true')
    },

    selectProject: waitFor('select-tt-project', async function (project) {
      if (this.$store.state.timetablePlanner.headers['version-uuid'] === project.id) return

      await this.$store.dispatch('timetablePlanner/loadManagementTimetableProject', {
        id: project.id,
        school_id: this.school.id,
        entity: this.$route.params.entity,
        entity_id: getZeroSafeId(this.$route.params.id),
        // On the timetable page the selectedEntity watcher loads courses/lessons after navigation;
        // skip the heavy load here to avoid a request with the stale entity filter.
        skipHeavyEntities: this.$route.name === 'management-timetable',
      })

      this.$emit('project-switched')
      this.openNewTimetablePage()
    }),

    showNewTTProject() {
      this.newTTProject = new TimetableMeta({
        id: this.$uuid(),
        school_id: this.school.id,
      })
      this.showNewSchoolyear = true
    },
  },

  watch: {
    showPopup(newValue) {
      if (!newValue) return
      this.showNewTTProject()
    },
  },
}
</script>

<style scoped lang="scss">
:deep(.dropdown-toggle::after) {
  content: none !important;
}
</style>
